import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroupDirective, UntypedFormControl, ControlContainer, Validators, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { SelectBox } from 'cbc-shared-lib';
import { CodeConversionMstService } from '../../../../../../sg-base/sg-base-shared/services/codeConversionMst.service';
import { CodeValueConversionMstService } from '../../../../../../sg-base/sg-base-shared/services/codeValueConversionMst.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BehaviorSubject, throwError } from 'rxjs';
import { CodeConversionMstDto, CodeValueConversionMstDto } from 'sg-api';
import { CodeValue } from 'cbc-shared-lib';

/**
 * ファイルダウンロード条件
 * 
 * 入力項目はダウンロードの際にも必要となるため、検索ボタン押下後は条件を非活性とする
 * @export
 * @class FileDownloadConditionComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-file-download-condition',
  templateUrl: './sgkd017d-download-condition.component.html',
  styleUrls: ['./sgkd017d-download-condition.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgkd017dDownloadConditionComponent implements OnInit {
  /** 検索ボタン押下イベント */
  @Output() onClickSearchEvent = new EventEmitter<void>();
  /** クリアボタン押下イベント */
  @Output() onClickClearEvent = new EventEmitter<void>();

  /** 親コンポーネント保持領域 */
  parentForm: UntypedFormGroup | null = null;
  /** システムコード */
  systemCode: UntypedFormControl = this.fb.control({ value: '', disabled: false }, Validators.required);
  /** ファイル種別 */
  fileCategory: UntypedFormControl = this.fb.control({ value: '', disabled: false }, Validators.required);

  /** システムコード */
  systemCodeId: string = 'C402031';
  /** ファイル種別 */
  fileCategoryId: string = 'C300030';
  fileCategoryFilterId: string = 'C300010';
  // システムコードリスト
  systemCodeList: SelectBox[];
  // ファイル種別
  fileCategoryList: SelectBox[];
  result: SelectBox[];
  private onDestroy$ = new Subject();

  /** コード値変換マスターテーブル情報 */
  private _codeValueConversionMap$: BehaviorSubject<Map<string, Array<CodeValueConversionMstDto>>> =
  new BehaviorSubject<Map<string, Array<CodeValueConversionMstDto>>>(null);

  /** Setter */
  private set codeValueConversionMap(map: Map<string, Array<CodeValueConversionMstDto>>) {
    this._codeValueConversionMap$.next(map);
  }

  /** Getter */
  private get codeValueConversionMap() {
    return this._codeValueConversionMap$.getValue();
  }

  /** 検索ボタン非活性フラグ */
  disableSearch: boolean = false;

  /**
   * コンストラクタ
   * @memberof FileDownloadConditionComponent
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private codeConversionMstService: CodeConversionMstService,
    private codeValueConversionMstService: CodeValueConversionMstService
  ) { }

  /**
   * 初期処理
   * @memberof FileDownloadConditionComponent
   */
  ngOnInit(): void {

    this.codeConversionMstService.getCodeConversionMst([
      this.systemCodeId,
      this.fileCategoryId,
    ]).pipe(takeUntil(this.onDestroy$))
    .subscribe(
      (response) => {
        this.systemCodeList = this.sortedCodeSelectBox(response.get(this.systemCodeId));
        this.fileCategoryList = this.sortedCodeSelectBox(response.get(this.fileCategoryId));
      }
    )

    this.codeValueConversionMstService.getCodeValueConversionMst([
      "SZZZ001"
    ])
    .pipe(takeUntil(this.onDestroy$))
    .subscribe(map => {
      this.codeValueConversionMap = map; 
    });
    
    this.parentForm = this.parent.form;
    this.parentForm.addControl('systemCode', this.systemCode);
    this.parentForm.addControl('fileCategory', this.fileCategory);

    // ファイル種別を非活性化
    this.disabledFileCategory();
    // ファイル種別の制御処理を追加
    this.setSelectedValueChange();
  }

  /**
   * システムコード変更時のイベント処理
   * @memberof FileDownloadConditionComponent
   */
  setSelectedValueChange(): void {
    this.parentForm.get('systemCode').valueChanges.subscribe(value => {
      if (value == null || value == '') {
        // ファイル種別を非活性化
        this.disabledFileCategory();
      }
      else {
        this.enabledFileCategory();
        // システムコードに対応したファイル種別を設定
        this.fileCategoryList = this.getFileCategoryList(value);
      }
      // ファイル種別に設定されている値をクリア
      this.parentForm.get('fileCategory').setValue('');
    });
  }

  /**
   * ファイル種別プルダウンの表示項目制御
   * @param systemCode システムコードのvalue
   * @returns 
   * @memberof FileDownloadConditionComponent
   */
  getFileCategoryList(systemCode: string): SelectBox[] {
    this.codeConversionMstService.getCodeConversionMst([
      this.fileCategoryFilterId
    ])
    .pipe(takeUntil(this.onDestroy$))
    .subscribe(response => {
  
      const list = this.sortedCodeSelectBox(response.get(this.fileCategoryFilterId));
  
      this.result = list.filter(select =>
        this.getCodeChangeText("SZZZ001", select.value).convertedCode == systemCode
      );
  
      this.fileCategoryList = this.result;
    });
  
    return this.result;
  }

  /**
   * コードリストを項目並び順番でソートしたセレクトボックスを取得
   * @param array - コードリスト
   * @returns - 項目並び順番でソートされたコードリストのセレクトボックス
   *
   * @memberof Sgkd004dSearchConditionComponent
   */
  private sortedCodeSelectBox(array: Array<CodeConversionMstDto>): SelectBox[] {

    let returnArray: SelectBox[] = [];
    array
      .sort((x, y) => x.hyojiRowTurn.localeCompare(y.hyojiRowTurn))
      .forEach(x => returnArray.push({ value: x.userCode, viewValue: x.hyojiCont}));
    return returnArray;
  }

  /**
   * 検索押下処理
   * @memberof FileDownloadConditionComponent
   */
  onClickSearch(): void {
    this.disabledInput();
    this.onClickSearchEvent.emit();
  }

  /**
   * クリア押下処理
   * @memberof FileDownloadConditionComponent
   */
  onClickClear(): void {
    this.enabledInput();
    this.onClickClearEvent.emit();
  }

  /**
   * 画面項目非活性化
   * @memberof FileDownloadConditionComponent
   */
  disabledInput(): void {
    this.disableSearch = true;
    // disable/enableでvalueChangeイベントが発行されてしまうため、イベントを発行しないでようにemitEvent: falseを設定
    this.parentForm.get('systemCode').disable({ emitEvent: false });
    this.disabledFileCategory();
  }

  /**
   * 画面項目活性化
   * @memberof FileDownloadConditionComponent
   */
  enabledInput(): void {
    this.disableSearch = false;
    // disable/enableでvalueChangeイベントが発行されてしまうため、イベントを発行しないでようにemitEvent: falseを設定
    this.parentForm.get('systemCode').enable({ emitEvent: false });
    this.enabledFileCategory();
  }

  /**
   * ファイル種別非活性化
   * @memberof FileDownloadConditionComponent
   */
  disabledFileCategory(): void {
    this.parentForm.get('fileCategory').disable();
  }

  /**
   * ファイル種別活性化
   * @memberof FileDownloadConditionComponent
   */
  enabledFileCategory(): void {
    this.parentForm.get('fileCategory').enable();
  }
  
   /**
   * コード値変換情報から変換識別種別コードと変換前コード値1を元に、
   * 変換後コード値1と変換後テキスト1を返却する
   * @param {string} typeCode 変換識別種別コード
   * @param {string} originCode1 変換前コード値1
   * @return {*}  {CodeValue}
   * @memberof InitialInfoService
   */
   public getCodeChangeText(typeCode: string, originCode1: string): CodeValue {
    let codeValue: CodeValue = {
      convertedCode: '',
      convertedText: ''
    };

    if (this.codeValueConversionMap.has(typeCode)) {
      for (let data of this.codeValueConversionMap.get(typeCode)) {
        if (data.cnversOriginCode1 == originCode1) {
          codeValue.convertedCode = data.cnversAfterCode1;
          codeValue.convertedText = data.cnversAfterText1;
          break;
        }
      }
    }
    return codeValue;
  }
}
